import { useSupabaseClient } from '~/plugins/supabase';

const LOCAL_KEY = 'cached_card_types';
const META_KEY = 'cached_card_types_meta';

export const useCardTypes = async () => {
  const cardTypes = useState('cardTypes', () => []);

  // ✅ If already loaded, return cached in-memory state
  if (cardTypes.value.length) return cardTypes.value;

  const supabase = useSupabaseClient();

  let localCache = null;
  let metaCache = null;

  if (process.client) {
    localCache = localStorage.getItem(LOCAL_KEY);
    metaCache = localStorage.getItem(META_KEY);
  }

  const localUpdated = metaCache
    ? JSON.parse(metaCache).last_updated
    : '1970-01-01T00:00:00Z';

  const { data: latestRow, error: latestErr } = await supabase
    .from('card_type')
    .select('last_updated')
    .order('last_updated', { ascending: false })
    .limit(1)
    .single();

  if (latestErr) {
    console.warn('Could not fetch last_updated:', latestErr);
    if (localCache && process.client) {
      cardTypes.value = JSON.parse(localCache);
    }
    return cardTypes.value;
  }

  const remoteUpdated = latestRow.last_updated;

  const shouldFetch =
    new Date(remoteUpdated) > new Date(localUpdated) || !localCache;

  if (shouldFetch) {
    const { data: freshData, error } = await supabase
      .from('card_type')
      .select('id, name, description, last_updated,category,label');

    if (error) {
      console.error('Failed to fetch card_type:', error);
      if (localCache && process.client) {
        cardTypes.value = JSON.parse(localCache);
      }
      return cardTypes.value;
    }

    if (process.client) {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(freshData));
      localStorage.setItem(
        META_KEY,
        JSON.stringify({ last_updated: remoteUpdated })
      );
    }

    cardTypes.value = freshData;
    return cardTypes.value;
  }

  if (process.client && localCache) {
    cardTypes.value = JSON.parse(localCache);
  }

  return cardTypes.value;
};

export const useTemplateToCardTypeID = async () => {
  const cardTypes = await useCardTypes();

  const map = {};

  for (const card of cardTypes) {
    const key = card.name?.toLowerCase().replace(/\s+/g, '-');
    if (key && typeof card.id === 'number') {
      map[key] = card.id;
    }
  }

  return map;
};
export const hasCanvas = async (cardType) => {
  const cardTypes = await useCardTypes();
  const canvasCardTypes = [
    'business-model',
    'swot-analysis',
    'goals-kpis',
    // 'goal',
    // 'kpi',
    'api',
    'application',
    'comms-matrix',
  ];

  // Check if cardType is a number or numeric string
  const numericId =
    typeof cardType === 'number'
      ? cardType
      : !isNaN(cardType) && !isNaN(parseFloat(cardType))
        ? parseInt(cardType, 10)
        : null;

  if (numericId !== null) {
    // Find the card by ID and check if its label is in canvasCardTypes
    const card = cardTypes.find((card) => card.id === numericId);
    return card ? canvasCardTypes.includes(card.label) : false;
  }

  // Otherwise treat as a label string
  return canvasCardTypes.includes(cardType);
};

export const getCanvasTableName = async (cardType) => {
  const cardTypes = await useCardTypes();
  const canvasCardTypes = {
    'business-model': 'business_model_card',
    'swot-analysis': 'swot_card',
    'goals-kpis': 'goals_kpis_card',
    goal: 'goals_card',
    kpi: 'kpis_card',
    api: 'api_card',
    application: 'application_card',
    'comms-matrix': 'comms_matrix_card',
  };

  // Check if cardType is a number or numeric string
  const numericId =
    typeof cardType === 'number'
      ? cardType
      : !isNaN(cardType) && !isNaN(parseFloat(cardType))
        ? parseInt(cardType, 10)
        : null;

  if (numericId !== null) {
    // Find the card by ID and get its label
    const card = cardTypes.find((card) => card.id === numericId);
    return card ? canvasCardTypes[card.label] : null;
  }

  // Otherwise treat as a label string
  return canvasCardTypes[cardType] || null;
};
